import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import {
  DollarSign, TrendingUp, Users, Ban, AlarmClock, AlertTriangle, RefreshCw,
  Plus, X, Save, ArrowLeft, Loader2, Trophy, Trash2, ChevronRight, CircleCheck,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import KpiTile from './super/KpiTile';
import { useTabParam } from './shared/useTabParam';
import PhoneInput from './shared/PhoneInput';
import MoneyInput from './shared/MoneyInput';
import { formatDate, fromApi } from './kit/time';
import { errorMessage } from './errors';
import LongTextCell from '@/components/LongTextCell';

const STAGES = [
  { key: 'new',         label: 'New',         color: '#94a3b8' },
  { key: 'qualified',   label: 'Qualified',   color: '#3b82f6' },
  { key: 'demo',        label: 'Demo',        color: '#6366f1' },
  { key: 'proposal',    label: 'Proposal',    color: '#8b5cf6' },
  { key: 'negotiation', label: 'Negotiation', color: '#f59e0b' },
  { key: 'won',         label: 'Won',         color: '#22c55e' },
  { key: 'lost',        label: 'Lost',        color: '#ef4444' },
];

const fmt$ = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt$fine = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const fmtDate = (iso) => iso ? formatDate(iso) : '—';

const STATUS_COLORS = {
  active:    { bg: '#8DC63F20', color: '#4d7a1e', label: 'Active' },
  trialing:  { bg: '#3b82f620', color: '#1d4ed8', label: 'Trial' },
  past_due:  { bg: '#f59e0b20', color: '#b45309', label: 'Past Due' },
  canceled:  { bg: '#ef444420', color: '#b91c1c', label: 'Canceled' },
  paused:    { bg: '#94a3b820', color: '#475569', label: 'Paused' },
};

const KPI = KpiTile;

export default function RevenueDashboardPage() {
  const [tab, setTab] = useTabParam('subscriptions', { valid: ['subscriptions', 'opportunities', 'ambassadors'] });
  const [rev, setRev] = useState(null);
  const [opps, setOpps] = useState(null);
  const [editing, setEditing] = useState(null); // tenant row being edited
  const [oppEditor, setOppEditor] = useState(null); // opportunity being created/edited

  const load = async () => {
    try {
      const [r, o] = await Promise.all([
        api.get('/super/revenue'),
        api.get('/super/opportunities'),
      ]);
      setRev(r.data);
      setOpps(o.data);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.response?.data?.detail || 'Owner-admin only' });
    }
  };
  useEffect(() => { load(); }, []);

  if (!rev || !opps) return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading revenue…</div>;

  const kpis = rev.kpis;
  const oppKpis = opps.kpis;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="revenue-dashboard-page">
      <div className="flex items-center justify-between mb-4">
        <Link to="/portal/super-admin" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm" data-testid="back-to-super-admin">
          <ArrowLeft size={14} /> Back to Super Admin
        </Link>
        <button onClick={load} data-testid="refresh-revenue" className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Header banner */}
      <div className="rounded-2xl p-6 mb-6" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
        <div className="text-[10px] uppercase tracking-widest text-white/60">Owner Console</div>
        <div className="font-serif text-3xl font-bold text-white">Revenue &amp; Sales</div>
        <div className="mt-1 text-sm text-white/70">Track MRR, ARR, cancellations and the sales pipeline for BrokerForYou as a SaaS.</div>
      </div>

      {/* KPI Grid — 6 tiles: MRR, ARR, Active, Renewing, At-Risk, Churn */}
      <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KPI label="MRR"        value={fmt$(kpis.mrr)}                sub="Monthly recurring"      icon={DollarSign}   color="#8DC63F" testid="kpi-mrr" />
        <KPI label="ARR"        value={fmt$(kpis.arr)}                sub="Annualized"             icon={TrendingUp}   color="#6BA82E" testid="kpi-arr" />
        <KPI label="Active"     value={kpis.active}                   sub={`${kpis.trialing} trial · ${kpis.past_due} past-due`} icon={Users} color="#070B5F" testid="kpi-active" />
        <KPI label="Renewing 30d" value={kpis.renewing_next_30d}      sub="Coming up"              icon={AlarmClock}   color="#3b82f6" testid="kpi-renewing" />
        <KPI label="At Risk"    value={kpis.at_risk}                  sub="Scheduled to cancel"    icon={AlertTriangle} color="#f59e0b" testid="kpi-at-risk" />
        <KPI label="Churn %"    value={`${kpis.churn_rate}%`}         sub={`${kpis.canceled_this_month} lost this mo`} icon={Ban} color="#ef4444" testid="kpi-churn" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-100">
        {[
          { k: 'subscriptions', l: `Subscriptions (${rev.subscriptions.length})` },
          { k: 'opportunities', l: `Opportunities (${oppKpis.total_opportunities})` },
          { k: 'ambassadors', l: 'Ambassadors' },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            className="text-[12px] font-semibold px-4 py-2.5 border-b-2 -mb-px transition"
            style={tab === t.k ? { borderColor: '#070B5F', color: '#070B5F' } : { borderColor: 'transparent', color: '#64748b' }}
          >{t.l}</button>
        ))}
      </div>

      {tab === 'subscriptions' && (
        <SubscriptionsTable rows={rev.subscriptions} onEdit={setEditing} onReload={load} />
      )}

      {tab === 'opportunities' && (
        <OpportunitiesBoard
          data={opps}
          onEdit={setOppEditor}
          onNew={() => setOppEditor({ brokerage_name: '', stage: 'new', monthly_value: '', plan_interested_in: 'pro' })}
          onReload={load}
        />
      )}

      {tab === 'ambassadors' && <AmbassadorsBoard />}

      {editing && <BillingEditor tenant={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {oppEditor && <OpportunityEditor opp={oppEditor} onClose={() => setOppEditor(null)} onSaved={() => { setOppEditor(null); load(); }} />}
    </div>
  );
}

/* ─── Subscriptions table with quick actions ───────────────────────────── */
function SubscriptionsTable({ rows, onEdit, onReload }) {
  const [filter, setFilter] = useState('all');
  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'at_risk') return rows.filter((r) => r.cancel_at && !r.canceled_at);
    if (filter === 'renewing_soon') {
      const now = Date.now(); const in30 = now + 30 * 86400000;
      return rows.filter((r) => {
        if (!r.current_period_end) return false;
        const cpe = fromApi(r.current_period_end).getTime();
        return cpe >= now && cpe <= in30;
      });
    }
    return rows.filter((r) => (r.status || 'active') === filter);
  }, [rows, filter]);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap gap-1.5">
        {[
          { k: 'all', l: `All (${rows.length})` },
          { k: 'active', l: 'Active' },
          { k: 'trialing', l: 'Trialing' },
          { k: 'past_due', l: 'Past Due' },
          { k: 'renewing_soon', l: 'Renewing 30d' },
          { k: 'at_risk', l: 'At Risk' },
          { k: 'canceled', l: 'Canceled' },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={`sub-filter-${t.k}`}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md border transition"
            style={filter === t.k
              ? { background: '#070B5F', color: 'white', borderColor: '#070B5F' }
              : { background: 'white', color: '#070B5F', borderColor: '#e2e8f0' }}
          >{t.l}</button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr className="text-[10px] uppercase tracking-widest text-slate-500">
              <th className="text-left px-4 py-2.5 font-semibold">Tenant</th>
              <th className="text-left px-3 py-2.5 font-semibold">Tier</th>
              <th className="text-left px-3 py-2.5 font-semibold">Status</th>
              <th className="text-right px-3 py-2.5 font-semibold">Price</th>
              <th className="text-right px-3 py-2.5 font-semibold">MRR</th>
              <th className="text-left px-3 py-2.5 font-semibold">Renews</th>
              <th className="text-left px-3 py-2.5 font-semibold">Auto-Renew</th>
              <th className="text-right px-3 py-2.5 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-sm text-slate-500" data-testid="subs-empty">No tenants match this filter.</td></tr>
            )}
            {filtered.map((r) => {
              const sc = STATUS_COLORS[r.status || 'active'] || STATUS_COLORS.active;
              const isAtRisk = r.cancel_at && !r.canceled_at;
              return (
                <tr key={r.tenant_id} className="hover:bg-slate-50/60" data-testid={`sub-row-${r.tenant_id}`}>
                  <td className="px-4 py-2.5">
                    <LongTextCell id={`tenant-${r.tenant_id}`} text={r.name} lines={2} maxWidth={220} className="font-semibold text-slate-800" />
                    <div className="text-[10px] text-slate-400 font-mono">{r.slug}</div>
                  </td>
                  <td className="px-3 py-2.5"><span className="text-[11px] font-semibold uppercase text-slate-600">{r.subscription_tier}</span></td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.color }}>
                      {sc.label}
                    </span>
                    {isAtRisk && <div className="text-[10px] text-amber-600 mt-1">↓ Cancels {fmtDate(r.cancel_at)}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-700">{fmt$fine(r.plan_price_monthly)} <span className="text-[10px] text-slate-400">/ {r.billing_cycle}</span></td>
                  <td className="px-3 py-2.5 text-right font-semibold" style={{ color: '#070B5F' }}>{fmt$fine(r.mrr)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{fmtDate(r.current_period_end)}</td>
                  <td className="px-3 py-2.5">
                    {r.auto_renew
                      ? <span className="text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1"><CircleCheck size={11} /> On</span>
                      : <span className="text-[11px] text-slate-500 font-semibold">Off</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => onEdit(r)}
                      data-testid={`sub-edit-${r.tenant_id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md text-white hover:brightness-110"
                      style={{ background: '#070B5F' }}
                    >Edit <ChevronRight size={11} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Billing / cancel / renew editor ──────────────────────────────────── */
function BillingEditor({ tenant, onClose, onSaved }) {
  const [form, setForm] = useState({
    plan_price_monthly: tenant.plan_price_monthly || '',
    billing_cycle: tenant.billing_cycle || 'monthly',
    subscription_tier: tenant.subscription_tier || 'pro',
    status: tenant.status || 'active',
    current_period_end: tenant.current_period_end ? tenant.current_period_end.substring(0, 10) : '',
    auto_renew: tenant.auto_renew ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/super/tenants/${tenant.tenant_id}/billing`, {
        ...form,
        plan_price_monthly: form.plan_price_monthly === '' ? null : form.plan_price_monthly,
        current_period_end: form.current_period_end || null,
      });
      toast({ title: 'Billing updated' });
      onSaved();
    } catch (e) {
      toast({ title: 'Save failed', description: errorMessage(e, 'Try again') });
    } finally { setBusy(false); }
  };

  const cancelSub = async (immediate = false) => {
    if (!(await confirmDialog(immediate
      ? { title: 'Mark this subscription canceled now?', description: 'Status becomes canceled today and auto-renew turns off. It does not sign anyone out or lock the workspace.', confirmText: 'Cancel now', cancelText: 'Keep subscription', destructive: true }
      : { title: 'Schedule cancellation at period end?', description: 'Auto-renew turns off now. The subscription is marked canceled on the period-end date. Nobody is signed out or locked out.', confirmText: 'Schedule', cancelText: 'Keep subscription' }))) return;
    setBusy(true);
    try {
      await api.post(`/super/tenants/${tenant.tenant_id}/cancel`, { reason: cancelReason || null, immediate });
      toast({ title: immediate ? 'Subscription marked canceled' : 'Scheduled to cancel at period end' });
      onSaved();
    } catch (e) {
      toast({ title: 'Failed', description: errorMessage(e, 'Try again') });
    } finally { setBusy(false); }
  };

  const renewSub = async (months) => {
    setBusy(true);
    try {
      await api.post(`/super/tenants/${tenant.tenant_id}/renew`, { months });
      toast({ title: `Renewed +${months} month${months > 1 ? 's' : ''}` });
      onSaved();
    } catch (e) {
      toast({ title: 'Failed', description: errorMessage(e, 'Try again') });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="billing-editor">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">Edit Subscription</div>
            <div className="font-serif text-xl font-bold text-white">{tenant.name}</div>
          </div>
          <button onClick={onClose} data-testid="billing-editor-close" className="p-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Monthly Price ($)</Label>
              <MoneyInput cents value={form.plan_price_monthly} onChange={(n) => setForm({ ...form, plan_price_monthly: n })} className="mt-1" data-testid="billing-price" />
            </div>
            <div>
              <Label>Billing Cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                <SelectTrigger className="mt-1" data-testid="billing-cycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tier</Label>
              <Select value={form.subscription_tier} onValueChange={(v) => setForm({ ...form, subscription_tier: v })}>
                <SelectTrigger className="mt-1" data-testid="billing-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1" data-testid="billing-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Current Period Ends</Label>
              <Input type="date" value={form.current_period_end} onChange={(e) => setForm({ ...form, current_period_end: e.target.value })} className="mt-1" data-testid="billing-period-end" />
            </div>
            <div className="flex items-end gap-2 pb-1">
              <input type="checkbox" id="auto_renew" checked={form.auto_renew} onChange={(e) => setForm({ ...form, auto_renew: e.target.checked })} data-testid="billing-auto-renew" />
              <label htmlFor="auto_renew" className="text-sm font-semibold">Auto-Renew</label>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4 space-y-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-red-600">Danger Zone</div>
            <Textarea rows={2} placeholder="Cancellation reason (optional)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="billing-cancel-reason" />
            <div className="flex flex-wrap gap-2">
              <button onClick={() => cancelSub(false)} data-testid="billing-cancel-at-period" disabled={busy} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50">Cancel at period end</button>
              <button onClick={() => cancelSub(true)}  data-testid="billing-cancel-now" disabled={busy} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50">Cancel immediately</button>
              <div className="ml-auto flex gap-2">
                <button onClick={() => renewSub(1)}  data-testid="billing-renew-1"  disabled={busy} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50">+1 mo</button>
                <button onClick={() => renewSub(12)} data-testid="billing-renew-12" disabled={busy} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50">+12 mo</button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[13px] font-semibold px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-100">Close</button>
          <button onClick={save} disabled={busy} data-testid="billing-save" className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Opportunities kanban ─────────────────────────────────────────────── */
function OpportunitiesBoard({ data, onEdit, onNew, onReload }) {
  return (
    <div>
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <KPI label="Pipeline" value={fmt$(data.kpis.pipeline_value)} sub={`${data.kpis.total_opportunities} deals`} icon={TrendingUp} color="#070B5F" testid="kpi-pipeline" />
        <KPI label="Weighted" value={fmt$(data.kpis.weighted_pipeline)} sub="× stage prob." icon={TrendingUp} color="#6BA82E" testid="kpi-weighted" />
        <KPI label="Won MRR"  value={fmt$(data.kpis.won_mrr)} sub={`${data.kpis.won_count} closed`} icon={Trophy} color="#22c55e" testid="kpi-won" />
        <KPI label="Lost"     value={data.kpis.lost_count} sub="Not converted" icon={Ban} color="#ef4444" testid="kpi-lost" />
      </div>

      <div className="flex justify-end mb-3">
        <button onClick={onNew} data-testid="new-opportunity" className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white shadow-sm hover:brightness-110" style={{ background: 'linear-gradient(135deg, #6BA82E 0%, #8DC63F 100%)' }}>
          <Plus size={12} /> New Opportunity
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {STAGES.map((s) => {
          const items = data.board[s.key] || [];
          const total = items.reduce((sum, i) => sum + (Number(i.monthly_value) || 0), 0);
          return (
            <div key={s.key} className="bg-slate-50 rounded-xl border border-slate-100 min-h-[220px]" data-testid={`opp-col-${s.key}`}>
              <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                  <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: s.color }}>{s.label}</span>
                </div>
                <span className="text-[10px] font-semibold text-slate-500">{items.length}</span>
              </div>
              <div className="p-2 space-y-2">
                {items.length === 0 && <div className="text-[10px] text-slate-400 text-center py-4">Empty</div>}
                {items.map((o) => (
                  <button
                    key={o.opportunity_id}
                    onClick={() => onEdit(o)}
                    data-testid={`opp-card-${o.opportunity_id}`}
                    className="w-full text-left p-2.5 bg-white rounded-lg border border-slate-100 hover:border-slate-300 hover:shadow-sm transition"
                  >
                    <div className="font-semibold text-[12px] text-slate-800 truncate">{o.brokerage_name}</div>
                    {o.contact_name && <div className="text-[10px] text-slate-400 truncate">{o.contact_name}</div>}
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-[11px] font-semibold" style={{ color: s.color }}>{fmt$(o.monthly_value)}</span>
                      {o.plan_interested_in && <span className="text-[9px] uppercase tracking-wider text-slate-400">{o.plan_interested_in}</span>}
                    </div>
                  </button>
                ))}
              </div>
              {items.length > 0 && (
                <div className="px-3 py-1.5 border-t border-slate-100 text-[10px] font-semibold text-slate-500 text-right">
                  {fmt$(total)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OpportunityEditor({ opp, onClose, onSaved }) {
  const isNew = !opp.opportunity_id;
  const [form, setForm] = useState({
    brokerage_name: opp.brokerage_name || '',
    contact_name: opp.contact_name || '',
    contact_email: opp.contact_email || '',
    contact_phone: opp.contact_phone || '',
    plan_interested_in: opp.plan_interested_in || 'pro',
    monthly_value: opp.monthly_value ?? '',
    stage: opp.stage || 'new',
    source: opp.source || '',
    notes: opp.notes || '',
    expected_close_date: opp.expected_close_date ? opp.expected_close_date.substring(0, 10) : '',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.brokerage_name.trim()) { toast({ title: 'Brokerage name required' }); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        monthly_value: form.monthly_value === '' ? null : form.monthly_value,
        expected_close_date: form.expected_close_date || null,
      };
      if (isNew) await api.post('/super/opportunities', payload);
      else await api.patch(`/super/opportunities/${opp.opportunity_id}`, payload);
      toast({ title: isNew ? 'Opportunity created' : 'Saved' });
      onSaved();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!(await confirmDialog({ title: 'Delete this opportunity?', confirmText: 'Delete', destructive: true }))) return;
    setBusy(true);
    try {
      await api.delete(`/super/opportunities/${opp.opportunity_id}`);
      toast({ title: 'Deleted' });
      onSaved();
    } catch (e) {
      toast({ title: 'Failed', description: 'Try again' });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="opp-editor">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #6BA82E 0%, #8DC63F 100%)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/80">Sales Opportunity</div>
            <div className="font-serif text-xl font-bold text-white">{isNew ? 'New Opportunity' : opp.brokerage_name}</div>
          </div>
          <button onClick={onClose} data-testid="opp-editor-close" className="p-2 rounded-lg text-white/80 hover:bg-white/10 hover:text-white"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Brokerage Name *</Label><Input value={form.brokerage_name} onChange={(e) => setForm({ ...form, brokerage_name: e.target.value })} className="mt-1" data-testid="opp-name" /></div>
            <div><Label>Contact Name</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Contact Email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1" /></div>
            <div><Label>Contact Phone</Label><PhoneInput value={form.contact_phone} onChange={(d) => setForm({ ...form, contact_phone: d })} className="mt-1" /></div>
            <div>
              <Label>Plan Interested In</Label>
              <Select value={form.plan_interested_in} onValueChange={(v) => setForm({ ...form, plan_interested_in: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Est. Monthly Value</Label><MoneyInput value={form.monthly_value} onChange={(n) => setForm({ ...form, monthly_value: n })} className="mt-1" data-testid="opp-mrr" /></div>
            <div>
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className="mt-1" data-testid="opp-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="referral, cold outbound, LinkedIn…" className="mt-1" /></div>
            <div><Label>Expected Close Date</Label><Input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} className="mt-1" /></div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
          <div>
            {!isNew && (
              <button onClick={del} data-testid="opp-delete" className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1">
                <Trash2 size={12} /> Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-[13px] font-semibold px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-100">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="opp-save" className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Open referrals: hand steps for the owner (ticket 207) ────────────── */
export function OpenReferrals() {
  const [rows, setRows] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get('/referrals/admin/open')
    .then((r) => setRows(r.data.referrals || []))
    .catch(() => setRows([]));

  useEffect(() => {
    load();
    api.get('/tenants/admin/all').then((r) => setTenants(r.data.tenants || [])).catch(() => setTenants([]));
  }, []);

  const markSignedUp = async (row) => {
    const tenantId = pick[row.referral_id];
    if (!tenantId) return;
    setBusy(row.referral_id);
    try {
      await api.post('/referrals/mark-signed-up', { referral_id: row.referral_id, new_tenant_id: tenantId });
      toast({ title: 'Marked signed up' });
      await load();
    } catch (e) {
      toast({ title: 'Could not mark signed up', description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const markPaid = async (row) => {
    const ok = await confirmDialog({
      title: 'Mark this referral paid?',
      description: `${row.referrer_name || row.referrer_email || 'The referrer'} gets one free month of their plan as a Stripe credit. This cannot be undone.`,
      confirmText: 'Mark paid',
    });
    if (!ok) return;
    setBusy(row.referral_id);
    try {
      const { data } = await api.post('/referrals/convert', { tenant_id: row.new_tenant_id });
      toast(data.stripe?.applied
        ? { title: 'Referral paid, credit applied on Stripe' }
        : { title: 'Referral marked paid, Stripe credit not applied', description: `Reason: ${data.stripe?.reason || 'unknown'}. Apply it by hand.` });
      await load();
    } catch (e) {
      toast({ title: 'Could not mark paid', description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  if (rows === null) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-4" data-testid="open-referrals">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Open referrals</div>
        <div className="text-sm text-slate-500">Waiting on a manual step: link the tenant they became, then mark paid.</div>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500" data-testid="open-referrals-empty">No open referrals.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-2.5 font-semibold">Referrer</th>
                <th className="text-left px-3 py-2.5 font-semibold">Invitee</th>
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                <th className="text-left px-3 py-2.5 font-semibold">Brokerage</th>
                <th className="text-right px-4 py-2.5 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.referral_id} data-testid={`open-referral-${row.referral_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{row.referrer_name || 'Unknown'}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{row.referrer_email}</div>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{row.invitee_email || '—'}</td>
                  <td className="px-3 py-3 text-slate-700 capitalize">{(row.status || '').replace('_', ' ')}</td>
                  <td className="px-3 py-3 text-slate-700">{row.new_tenant_name || row.new_tenant_id || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {row.status === 'pending' && (
                      <div className="flex items-center justify-end gap-2">
                        <Select value={pick[row.referral_id] || ''} onValueChange={(v) => setPick({ ...pick, [row.referral_id]: v })}>
                          <SelectTrigger className="h-8 w-[180px] text-[12px]" aria-label="Brokerage they signed up as" data-testid={`open-referral-tenant-${row.referral_id}`}>
                            <SelectValue placeholder="Pick brokerage…" />
                          </SelectTrigger>
                          <SelectContent>
                            {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button
                          onClick={() => markSignedUp(row)}
                          disabled={!pick[row.referral_id] || busy === row.referral_id}
                          data-testid={`open-referral-signed-up-${row.referral_id}`}
                          className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40"
                        >
                          Mark signed up
                        </button>
                      </div>
                    )}
                    {row.status === 'signed_up' && (
                      <button
                        onClick={() => markPaid(row)}
                        disabled={busy === row.referral_id}
                        data-testid={`open-referral-paid-${row.referral_id}`}
                        className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-40"
                        style={{ background: '#6BA82E' }}
                      >
                        Mark paid
                      </button>
                    )}
                    {row.status === 'converting' && (
                      <span className="text-[11px] text-amber-600">Stuck mid-credit — check Stripe first</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── All-Time Ambassador Leaderboard ─────────────────────────────────── */
function AmbassadorsBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/referrals/leaderboard/all-time')
      .then((r) => setData(r.data))
      .catch((e) => setError(errorMessage(e, 'Could not load the leaderboard.')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> Loading ambassadors…</div>;
  if (error) return <div className="p-10 text-center text-sm text-red-600" role="alert" data-testid="ambassadors-error">{error}</div>;
  const rows = data?.ranked || [];

  return (
    <div data-testid="ambassadors-board">
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <KPI label="Total Ambassadors" value={rows.length} sub="all-time" icon={Trophy} color="#070B5F" testid="kpi-ambassadors-count" />
        <KPI label="Lifetime Conversions" value={data.total_conversions} sub="paid brokerages" icon={CircleCheck} color="#6BA82E" testid="kpi-lifetime-conversions" />
        <KPI label="Credits Issued" value={fmt$(data.total_credit_dollars)} sub="lifetime $" icon={DollarSign} color="#8DC63F" testid="kpi-credits-issued" />
      </div>

      <OpenReferrals />

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>All-Time Top Referrers</div>
          <div className="text-sm text-slate-500">Ranked by lifetime conversions · then credit dollars</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="ambassadors-empty">No conversions yet — once someone's referral pays, they show up here.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-4 py-2.5 font-semibold w-12">Rank</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Ambassador</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Conversions</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Referrals</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Conv. Rate</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Credit $</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Last Conv.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => {
                  const rank = i + 1;
                  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
                  return (
                    <tr key={r.user_id} className="hover:bg-slate-50/60" data-testid={`ambassador-row-${rank}`}>
                      <td className="px-4 py-3 text-center text-lg">{medal}</td>
                      <td className="px-3 py-3">
                        <div className="font-semibold text-slate-800">{r.name}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{r.email}</div>
                      </td>
                      <td className="px-3 py-3 text-right font-semibold" style={{ color: '#6BA82E' }}>{r.conversions}</td>
                      <td className="px-3 py-3 text-right text-slate-700">{r.total_referrals}</td>
                      <td className="px-3 py-3 text-right text-slate-700">{r.conversion_rate}%</td>
                      <td className="px-3 py-3 text-right font-semibold" style={{ color: '#8DC63F' }}>{fmt$fine(r.credit_amount_cents / 100)}</td>
                      <td className="px-3 py-3 text-right text-[11px] text-slate-500 whitespace-nowrap">{r.last_conversion_at ? formatDate(r.last_conversion_at) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
